// UI state for Tilgong Manager: which tab is active, which tenant we're "acting as", and which
// tenant is the internal/admin tenant. This is the tested logic unit (the coverage gate covers
// src/state); App.tsx is a thin presentational shell around it.
//
// "Acting as" models login without real auth (the backend is Anonymous; enforcement is upstream).
// Acting as the internal tenant unlocks the admin all-tenants view (isAdmin); acting as any other
// tenant scopes the app to that tenant's offerings only.

import type { MyGrantResponse } from '../api/types';

export type Tab = 'tenants' | 'offerings' | 'admins' | 'sso' | 'payment' | 'users';

export interface AppState {
  /** The active tab. */
  tab: Tab;
  /** The tenant id we are acting as, or null when no identity is chosen. */
  actingAs: string | null;
  /** The designated internal/admin tenant id, or null when unknown/none designated. */
  internalTenantId: string | null;
  /**
   * The signed-in account's own admin grants (the tenant-admin path, from GET /api/my-grants), or
   * null when not resolved — a global admin never fetches them and acts via the Tenants tab instead.
   */
  grants: MyGrantResponse[] | null;
}

export type AppAction =
  | { type: 'setInternalTenant'; tenantId: string | null }
  | { type: 'actAs'; tenantId: string }
  | { type: 'setGrants'; grants: MyGrantResponse[] }
  | { type: 'selectTenant'; tenantId: string }
  | { type: 'switchTenant' }
  | { type: 'selectTab'; tab: Tab }
  | { type: 'signOut' };

export const initialState: AppState = {
  tab: 'offerings',
  actingAs: null,
  internalTenantId: null,
  grants: null,
};

/** True when the acting-as identity is the internal/admin tenant (so the admin tab is unlocked). */
export function isAdmin(state: AppState): boolean {
  return state.actingAs !== null && state.actingAs === state.internalTenantId;
}

export function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'setInternalTenant':
      return { ...state, internalTenantId: action.tenantId };
    case 'actAs':
      // Land an admin on the tenants tab; a normal tenant only has offerings.
      return {
        ...state,
        actingAs: action.tenantId,
        tab: action.tenantId === state.internalTenantId ? 'tenants' : 'offerings',
      };
    case 'setGrants':
      // A tenant admin's own grants: a single tenant is entered directly; several defer to the
      // tenant picker (actingAs stays null until one is chosen).
      return action.grants.length === 1
        ? { ...state, grants: action.grants, actingAs: action.grants[0]!.tenantId, tab: 'offerings' }
        : { ...state, grants: action.grants };
    case 'selectTenant':
      // From the admin tenants list or the tenant picker: manage that specific tenant.
      return { ...state, actingAs: action.tenantId, tab: 'offerings' };
    case 'switchTenant':
      // Back to the tenant picker (multi-tenant admins only — the shell renders it when
      // grants are known and no tenant is being acted as).
      return { ...state, actingAs: null, tab: 'offerings' };
    case 'selectTab':
      return { ...state, tab: action.tab };
    case 'signOut':
      return { ...state, actingAs: null, tab: 'offerings', grants: null };
    default:
      return state;
  }
}
